/**
 * ProposalSharePage (PROP-E.2 AC-6) — the customer's branded proposal viewer at `/p/:token`.
 *
 * WHAT THIS REPLACES: the share link used to point at the backend, which 302'd straight at the PDF. The
 * customer got a raw file — no branding, nothing naming the sender, no context. The backend route is now
 * a shim that redirects here, so links already out with customers land on this page.
 *
 * REGISTER PUBLICLY. This mounts above the auth wrapper in App.tsx, next to `/sign`. The token is the
 * only authorisation and the RPC resolves everything from it; a customer has no account.
 *
 * NO JOULES BRANDING (UX-DR4, the rule the signing ceremony follows). The customer sees the company that
 * sent them the proposal.
 *
 * THE ACCENT RUNS THROUGH THE CONTRAST GUARD, ONCE (UX-DR3). The tenant supplies an arbitrary brand
 * colour and the Download control renders a label on it, so the pair must clear 4.5:1.
 * `contrastSafeAccent` handles the mid-tone band where neither a white nor a dark label passes on its own
 * — the case a naive white-or-dark choice ships failing.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileText, Loader2 } from 'lucide-react';

import { PRESET_THEMES, DEFAULT_THEME } from '@/types/theme';
import { contrastSafeAccent } from '@/lib/tenantAccent';
import {
  shareViewCopy,
  type ProposalShareContext,
  type ShareTenantBranding,
} from '@/lib/proposals/shareView';
import {
  getProposalShareContext,
  proposalShareDocumentUrl,
} from '@/services/proposalShareService';

/** Same lookup ThemeContext and the signing ceremony use. Falls back to the neutral Joules default so
 *  the Download control always has a legible accent. */
function resolveTenantAccent(branding: ShareTenantBranding): string {
  const all = [...PRESET_THEMES, ...((branding.custom_themes ?? []) as typeof PRESET_THEMES)];
  const match = branding.theme_id ? all.find((t) => t?.id === branding.theme_id) : undefined;
  return match?.colors?.primary ?? DEFAULT_THEME.colors.primary;
}

function BrandHeader({
  companyName,
  logo,
  accent,
  subline,
}: {
  companyName: string;
  logo: string | null;
  accent: string;
  subline: string;
}) {
  return (
    <header className="flex items-center gap-3">
      {logo ? (
        <img
          src={logo}
          alt={`${companyName} logo`}
          className="h-10 w-auto max-w-[160px] object-contain"
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
          style={{ backgroundColor: accent, color: contrastSafeAccent(accent).foreground }}
        >
          {(companyName.trim()[0] || '•').toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate text-base font-semibold text-slate-900">{companyName}</div>
        <div className="truncate text-sm text-slate-500">{subline}</div>
      </div>
    </header>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">{children}</div>
    </div>
  );
}

const ProposalSharePage = () => {
  const { token } = useParams<{ token: string }>();
  const [context, setContext] = useState<ProposalShareContext | null>(null);

  /**
   * ⚠️ ONE FETCH PER TOKEN, GUARDED BY A REF. The context RPC is VOLATILE — it counts the page open into
   * the rep's `view_count`. React 18 StrictMode double-invokes effects in development, and without this
   * guard every dev page load would count two views, so the rep's number would be quietly wrong in a way
   * that only reproduces locally.
   */
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setContext({ status: 'invalid' });
      return;
    }
    if (fetchedFor.current === token) return;
    fetchedFor.current = token;
    let alive = true;
    void getProposalShareContext(token).then((ctx) => {
      if (alive) setContext(ctx);
    });
    return () => {
      alive = false;
    };
  }, [token]);

  // Branded-neutral rather than blank: a white screen on a customer link reads as broken.
  if (!context) {
    return (
      <Shell>
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading your proposal…</span>
        </div>
      </Shell>
    );
  }

  const branding = context.status === 'ok' ? context.tenant : null;
  const companyName = branding?.company_name ?? 'The sender';
  const rawAccent = branding ? resolveTenantAccent(branding) : DEFAULT_THEME.colors.primary;
  const accent = contrastSafeAccent(rawAccent);
  const copy = shareViewCopy(context.status, branding?.company_name);

  if (!copy.showsDocument) {
    return (
      <Shell>
        <BrandHeader companyName={companyName} logo={branding?.logo ?? null} accent={rawAccent} subline="proposal" />
        <div className="rounded-lg border border-slate-200 bg-white px-6 py-10 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <h1 className="mt-3 text-lg font-semibold text-slate-900">{copy.headline}</h1>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-slate-600">{copy.body}</p>
        </div>
      </Shell>
    );
  }

  const documentUrl = proposalShareDocumentUrl(token as string);
  const proposalName = context.status === 'ok' ? context.proposal.name : null;
  const versionNo = context.status === 'ok' ? context.proposal.design_version_no : null;

  return (
    <Shell>
      <BrandHeader companyName={companyName} logo={branding?.logo ?? null} accent={rawAccent} subline="proposal" />

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-900">
              {proposalName || 'Your proposal'}
            </h1>
            {versionNo !== null && (
              <p className="mt-0.5 text-xs text-slate-500">Design version {versionNo}</p>
            )}
          </div>
          {/*
            DOWNLOAD IS THE GUARANTEE, the embed below is best-effort. Mobile browsers routinely refuse
            to render a PDF inline, so this control — not the iframe — is what makes the page work
            everywhere. `download` is deliberately absent: the target is a 302 to a signed URL, and a
            cross-origin `download` hint is ignored anyway, so letting the browser open it in a new tab
            is the honest behaviour.
          */}
          <a
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
            style={{ backgroundColor: accent.background, color: accent.foreground }}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download PDF
          </a>
        </div>

        <div className="p-2 sm:p-4">
          <iframe
            src={documentUrl}
            title={proposalName || 'Proposal'}
            className="h-[70vh] w-full rounded border border-slate-200 bg-slate-100"
          />
          <p className="mt-2 px-1 text-xs text-slate-500">
            Trouble viewing it here? Use <span className="font-medium">Download PDF</span> above.
          </p>
        </div>
      </div>
    </Shell>
  );
};

export default ProposalSharePage;
